import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, MessageCircle, Clock, MapPin, Check, AlertCircle, Calendar, DollarSign, Wrench, HelpCircle, Package, GraduationCap } from 'lucide-react';
import { api } from '../lib/api';
import SocialLinks from '../components/SocialLinks';
import useSEO from '../hooks/useSEO';

const initial = { name: '', email: '', phone: '', company: '', subject: '', message: '', machine_ref: '', industry: '', location: '', preferred_service: '' };

// Quick-tab intents — clicking pre-fills the subject line and message hint
const INTENT_TABS = [
    { key: 'quote', label: 'Machine Quote', icon: Wrench, subject: 'Equipment Quote Request', service: 'Machine Purchase', event: 'equipment_quote_click' },
    { key: 'parts', label: 'Parts & Accessories', icon: Package, subject: 'Parts & Accessories Question', service: 'Parts & Accessories', event: 'parts_click' },
    { key: 'rental', label: 'Equipment Rental', icon: Calendar, subject: 'Equipment Rental Inquiry', service: 'Equipment Rental', event: 'quote_click' },
    { key: 'training', label: 'Training', icon: GraduationCap, subject: 'Operator Training Inquiry', service: 'Operator Training', event: 'training_click' },
    { key: 'financing', label: 'Financing', icon: DollarSign, subject: 'Financing / TimePayment Question', service: 'Financing', event: 'finance_click' },
    { key: 'general', label: 'General', icon: HelpCircle, subject: 'General Inquiry', service: '', event: 'quote_click' },
];

const trackClick = (name, params = {}) => {
    if (typeof window !== 'undefined' && window.trackConversion) {
        window.trackConversion(name, params);
    }
};

const Contact = () => {
    const [form, setForm] = useState(initial);
    const [sending, setSending] = useState(false);
    const [ok, setOk] = useState(false);
    const [err, setErr] = useState('');
    const [activeIntent, setActiveIntent] = useState('quote');
    const location = useLocation();

    useSEO({
        title: 'Contact Dry Ice Bulls — Machine Quotes, Parts, Rentals & Training',
        description: 'Get a quote on a dry ice blasting machine, order parts, book a rental or operator training, or ask about financing. Call or text (984) 201-0767.',
    });

    // Prefill from either React Router state (legacy) or URL query params (new)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const subject = params.get('subject') || '';
        const machine = params.get('machine') || location.state?.machine || '';
        const service = params.get('service') || '';
        const interest = params.get('interest') || '';
        const message = params.get('message') || '';
        const preSubject = subject || (machine ? `Quote request: ${machine}` : '');

        setForm((f) => ({
            ...f,
            subject: preSubject,
            machine_ref: machine,
            message: message || f.message,
            preferred_service: service && f.preferred_service === '' ? mapServiceKey(service) : f.preferred_service,
        }));

        if (interest && INTENT_TABS.find((t) => t.key === interest)) {
            setActiveIntent(interest);
        } else if (machine) {
            setActiveIntent('quote');
        }
    }, [location.state, location.search]);

    const mapServiceKey = (k) => {
        const map = {
            'dry-ice': 'Machine Purchase', 'laser': 'Machine Purchase', 'sandblasting': 'Machine Purchase', 'pressure-washing': 'Machine Purchase',
            'parts': 'Parts & Accessories', 'rental': 'Equipment Rental', 'training': 'Operator Training', 'repair': 'Repair & Service',
        };
        return map[k] || k;
    };

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const applyIntent = (intent) => {
        setActiveIntent(intent.key);
        setForm((f) => ({
            ...f,
            subject: f.machine_ref && intent.key === 'quote'
                ? `Equipment quote: ${f.machine_ref}`
                : intent.subject,
            preferred_service: intent.service || f.preferred_service,
        }));
        trackClick(intent.event, { location: 'contact_tabs', intent: intent.key });
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!form.phone || form.phone.trim().length < 5) {
            setErr('Phone number is required. Emails often go to spam — we need a phone to reach you.');
            return;
        }
        setSending(true);
        try {
            await api.post('/contact', form);
            setOk(true);
            trackClick('form_submit', {
                form_id: 'contact_main',
                intent: activeIntent,
                preferred_service: form.preferred_service,
                machine: form.machine_ref,
            });
            // Also fire the intent-specific conversion so campaigns can measure separately
            const intentTab = INTENT_TABS.find((t) => t.key === activeIntent);
            if (intentTab) trackClick(intentTab.event, { location: 'contact_submit', intent: activeIntent });
            setForm(initial);
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            const message = Array.isArray(detail)
                ? detail.map((x) => x?.msg || JSON.stringify(x)).join(' ')
                : typeof detail === 'string' ? detail : 'Could not submit. Please call us instead.';
            setErr(message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="border-b border-neutral-200 bg-white">
                <div className="container-page pt-32 pb-16">
                    <div className="label-caps text-brand-primary mb-6">/ Contact</div>
                    <h1 className="font-display font-black uppercase text-5xl md:text-7xl text-neutral-900 leading-[0.95]">
                        Talk to an<br /><span className="text-brand-primary">equipment expert.</span>
                    </h1>
                    <p className="mt-6 text-neutral-600 max-w-2xl text-base md:text-lg leading-relaxed">
                        Tell us what you clean and how often. We&apos;ll recommend the right machine, nozzles and setup — or help with parts, rentals, training and financing.
                    </p>
                </div>
            </section>

            {/* Quick-intent tabs */}
            <section className="border-b border-neutral-200 bg-neutral-50">
                <div className="container-page py-6">
                    <div className="label-caps text-neutral-500 text-xs mb-3">/ What do you need today?</div>
                    <div className="flex flex-wrap gap-2" data-testid="contact-intent-tabs">
                        {INTENT_TABS.map((t) => {
                            const Icon = t.icon;
                            const active = activeIntent === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => applyIntent(t)}
                                    className={`inline-flex items-center gap-2 border px-4 py-2.5 label-caps text-xs transition-colors ${
                                        active
                                            ? 'bg-brand-primary text-white border-brand-primary'
                                            : 'bg-white text-neutral-700 border-neutral-300 hover:border-brand-primary hover:text-brand-primary'
                                    }`}
                                    data-testid={`contact-tab-${t.key}`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="container-page py-16 md:py-24 bg-white">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <aside className="lg:col-span-4 space-y-8">
                        <ContactBlock icon={Phone} label="Direct line" value="(984) 201-0767" href="tel:+19842010767" onClick={() => trackClick('phone_click', { location: 'contact_sidebar' })} testid="contact-phone" />
                        <ContactBlock icon={MessageCircle} label="WhatsApp" value="Message us" href="https://wa.me/19842010767" external testid="contact-whatsapp" />
                        <ContactBlock icon={Mail} label="Email" value="nc@dryicebulls.com" href="mailto:nc@dryicebulls.com" testid="contact-email" />
                        <ContactBlock icon={Clock} label="Hours" value={<>Mon–Fri: 07:00 – 18:00<br />24/7 emergency support</>} testid="contact-hours" />
                        <ContactBlock icon={MapPin} label="Coverage" value={<>Equipment & parts — shipped nationwide<br />Partners — across the US</>} testid="contact-coverage" />

                        <div className="border-l border-neutral-200 pl-6">
                            <div className="label-caps text-neutral-500 mb-3">Follow us</div>
                            <SocialLinks />
                        </div>
                    </aside>

                    <div className="lg:col-span-8">
                        {ok ? (
                            <div className="border border-brand-primary bg-brand-primary/5 p-8 md:p-10" data-testid="contact-success">
                                <Check className="h-10 w-10 text-brand-primary mb-4" />
                                <h3 className="font-display font-black uppercase text-2xl md:text-3xl text-neutral-900">Message received</h3>
                                <p className="mt-3 text-neutral-600">
                                    We&apos;ll be in touch on the phone number you provided. If we can&apos;t reach you,
                                    we&apos;ll follow up by email — so please check your spam folder just in case.
                                </p>
                                <button onClick={() => setOk(false)} className="mt-6 label-caps text-brand-primary hover:text-brand-primaryHover transition-colors" data-testid="contact-send-another">
                                    Send another message →
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="border border-neutral-200 bg-white p-6 md:p-10 space-y-6 shadow-sm" data-testid="contact-form">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Full name *" value={form.name} onChange={update('name')} required testid="contact-input-name" />
                                    <Field label="Company" value={form.company} onChange={update('company')} testid="contact-input-company" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Email *" type="email" value={form.email} onChange={update('email')} required testid="contact-input-email" />
                                    <Field
                                        label={<>Phone <span className="text-brand-primary">* required</span></>}
                                        type="tel"
                                        value={form.phone}
                                        onChange={update('phone')}
                                        placeholder="e.g. +1 555 123 4567"
                                        testid="contact-input-phone"
                                    />
                                </div>
                                <Field label="Subject" value={form.subject} onChange={update('subject')} testid="contact-input-subject" />
                                {form.machine_ref ? (
                                    <div className="border-l-2 border-brand-primary bg-neutral-50 pl-4 py-2 text-sm text-neutral-700">
                                        Regarding machine: <span className="font-bold text-neutral-900">{form.machine_ref}</span>
                                    </div>
                                ) : null}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <Field label="Industry" value={form.industry} onChange={update('industry')} placeholder="e.g. Automotive" testid="contact-input-industry" />
                                    <Field label="Location" value={form.location} onChange={update('location')} placeholder="e.g. Raleigh, NC" testid="contact-input-location" />
                                    <div>
                                        <label className="label-caps text-neutral-600 block mb-2">I&apos;m interested in</label>
                                        <select
                                            value={form.preferred_service}
                                            onChange={update('preferred_service')}
                                            className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-3 outline-none"
                                            data-testid="contact-input-preferred-service"
                                        >
                                            <option value="">— Not sure —</option>
                                            <option value="Machine Purchase">Buying a new machine</option>
                                            <option value="Used Machine">Buying a used machine</option>
                                            <option value="Parts & Accessories">Parts & accessories</option>
                                            <option value="Equipment Rental">Equipment rental</option>
                                            <option value="Operator Training">Operator training</option>
                                            <option value="Repair & Service">Repair & service</option>
                                            <option value="Financing">Financing</option>
                                            <option value="Partner Program">Partner program</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="label-caps text-neutral-600 block mb-2">Message *</label>
                                    <textarea
                                        rows={6}
                                        required
                                        value={form.message}
                                        onChange={update('message')}
                                        className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-4 outline-none"
                                        placeholder="Tell us about the job or the equipment you're interested in…"
                                        data-testid="contact-input-message"
                                    />
                                </div>

                                <div className="text-xs text-neutral-500 border-l-2 border-brand-primary pl-4">
                                    Your phone number is required. Some replies from us end up in spam and we don&apos;t want
                                    you thinking we ignored you — a phone number lets us confirm receipt fast.
                                </div>

                                {err && (
                                    <div className="flex items-start gap-3 border border-red-300 bg-red-50 text-red-700 p-4" data-testid="contact-error">
                                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                        <div>{err}</div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-8 py-4 label-caps transition-colors"
                                    data-testid="contact-submit"
                                >
                                    {sending ? 'Sending…' : 'Send Message'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

const ContactBlock = ({ icon: Icon, label, value, href, external, onClick, testid }) => {
    const inner = (
        <>
            <Icon className="h-6 w-6 text-brand-primary mb-3" strokeWidth={1.5} />
            <div className="label-caps text-neutral-500 mb-1">{label}</div>
            <div className="text-neutral-900 font-display font-bold text-lg">{value}</div>
        </>
    );
    return (
        <div className="border-l border-neutral-200 pl-6" data-testid={testid}>
            {href ? (
                <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} onClick={onClick} className="block hover:text-brand-primary transition-colors">
                    {inner}
                </a>
            ) : inner}
        </div>
    );
};

const Field = ({ label, testid, ...props }) => (
    <div>
        <label className="label-caps text-neutral-600 block mb-2">{label}</label>
        <input
            {...props}
            className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-4 outline-none"
            data-testid={testid}
        />
    </div>
);

export default Contact;
